import {Edge, Node} from 'reactflow';

import {assert} from 'Asserts';
import {
  ProcedureEdgeNode,
  ProcedureEdgeType,
  ProcedureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  getConditionGroupGridRows,
  getRenderedGridRows,
} from 'studies/typed/shared/procedure-runs/conditions/conditionGroupGridRows';
import {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import {
  ConditionGroup,
  getMethodIdsImplementingConditionGroup,
  isPersistedCondition,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {hideFlowchartElementBeforeLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';

import {
  getActiveAndDownstreamMethodIds,
  type MethodIdAndName,
} from '../../procedure-runs/execution-status/activeMethodUtils';
import type {UnitOperationStatusType} from '../../procedure-runs/overview/procedureRunOverviewUtils';
import {
  createProcedureRunDesignFlowchartAddReplicateNode,
  getProcedureRunDesignFlowchartAddReplicateNodeId,
  ProcedureRunDesignFlowchartAddReplicateNodeData,
} from './ProcedureRunDesignFlowchartAddReplicateNode';
import {
  createProcedureRunDesignFlowchartColumnNode,
  FALLBACK_COLUMN_NODE_HEIGHT,
  ProcedureRunDesignFlowchartColumnNodeData,
} from './ProcedureRunDesignFlowchartColumnNode';
import {ConnectionRole, getNodeConnectionRoles} from './procedureRunDesignFlowchartConnectionUtils';
import {
  makeProcedureRunDesignFlowchartEdge,
  ProcedureRunDesignFlowchartEdgeData,
  ProcedureRunDesignFlowchartEdgeVariant,
} from './ProcedureRunDesignFlowchartEdge';
import {
  createProcedureRunDesignFlowchartGhostNode,
  getProcedureRunDesignFlowchartGhostNodeId,
  ProcedureRunDesignFlowchartGhostNodeData,
} from './ProcedureRunDesignFlowchartGhostNode';
import {
  createProcedureRunDesignFlowchartNode,
  FALLBACK_NODE_WIDTH,
  FALLBACK_NODE_HEIGHT,
  ProcedureRunDesignFlowchartNodeData,
  CellPresentationFields,
} from './ProcedureRunDesignFlowchartNode';
import {
  getProcedureRunDesignFlowchartNodeId,
  parseProcedureRunDesignFlowchartNodeId,
} from './ProcedureRunDesignFlowchartNodeIdUtils';

/** Maps a flowchart node id to its method task status. Write and read paths must use matching keys. */
export type StatusByNodeId = ReadonlyMap<string, UnitOperationStatusType>;

/** A directed link between two grid nodes. Both planning and execution edges reduce to this primitive. */
export interface FlowchartEdgeSpec {
  sourceNodeId: string;
  targetNodeId: string;
  /** Defaults to 'editable'. */
  variant?: ProcedureRunDesignFlowchartEdgeVariant;
}

export interface GridLayoutOptions {
  horizontalGap: number;
  verticalGap: number;
  startX: number;
  startY: number;
}

export interface MakeInitialNodesParams {
  conditionGroups: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  /** When false, persisted conditions are read-only and render a lock icon. */
  allowEditingPersistedConditions: boolean;
  procedureType: ProcedureType;
  factorsSetByMethodId?: Record<string, number>;
  activeMethodId?: string | null;
  statusByNodeId?: StatusByNodeId;
  showArchived?: boolean;
  /**
   * Seeds the edge-dependent showPersistentSourceHandle so the first paint is flicker-free. After that the
   * value is kept live off the rendered edges.
   */
  edgeSpecs?: Array<FlowchartEdgeSpec>;
}

/**
 * Creates the initial set of hidden nodes from domain data. This is the single place that maps
 * domain objects (conditions, methods) to ReactFlow nodes. All nodes start with opacity 0% so
 * ReactFlow can measure them before the first visible frame.
 */
export function makeInitialNodes({
  conditionGroups,
  methods,
  allowEditingPersistedConditions,
  procedureType,
  factorsSetByMethodId = {},
  activeMethodId = null,
  statusByNodeId,
  showArchived = false,
  edgeSpecs = [],
}: MakeInitialNodesParams): {
  columnNodes: Array<Node<ProcedureRunDesignFlowchartColumnNodeData>>;
  rows: Array<Array<Node<ProcedureRunDesignFlowchartNodeData>>>;
} {
  const activeMethodIndex =
    activeMethodId == null ? -1 : methods.findIndex((method) => method.id === activeMethodId);
  const columnNodes = methods.map((method, methodIndex) =>
    hideFlowchartElementBeforeLayout(
      createProcedureRunDesignFlowchartColumnNode({
        methodId: method.id,
        methodName: method.name,
        factorsSet: factorsSetByMethodId[method.id] ?? 0,
        procedureType,
        isActiveMethod: method.id === activeMethodId,
        isHighlighted: false,
        isUpstreamOfActiveMethod: activeMethodIndex > 0 && methodIndex < activeMethodIndex,
      })
    )
  );

  const {roleByNodeId, showPersistentSourceHandleByNodeId} = getNodeConnectionRoles({
    conditions: conditionGroups,
    methods,
    edges: edgeSpecs.map((spec) => ({source: spec.sourceNodeId, target: spec.targetNodeId})),
    activeMethodId,
    removedNodeIds: new Set(),
  });

  // A persisted group is read-only (lock icon) unless editing persisted conditions is allowed.
  const isLockedByConditionGroupIndex = conditionGroups.map(
    (conditionGroup) => !allowEditingPersistedConditions && isPersistedCondition(conditionGroup)
  );

  // Rows are keyed by backend replicate number, so gaps and partial archive fall out for free.
  // Archived cells are omitted when the toggle is off and rendered standalone when it is on.
  const rows: Array<Array<Node<ProcedureRunDesignFlowchartNodeData>>> = getRenderedGridRows(
    conditionGroups,
    methods,
    showArchived
  ).map(({conditionGroupIndex, replicateIndex, replicateNumber, cells}) => {
    const conditionGroup = conditionGroups[conditionGroupIndex];
    return cells.map(({methodId, isArchived, archiveReason}) => {
      const nodeId = getProcedureRunDesignFlowchartNodeId({
        conditionIndex: conditionGroupIndex,
        replicateIndex,
        methodId,
      });
      return hideFlowchartElementBeforeLayout(
        createProcedureRunDesignFlowchartNode({
          conditionIndex: conditionGroupIndex,
          replicateIndex,
          methodId,
          conditionName: conditionGroup.name,
          replicateNumber,
          conditionDescription: conditionGroup.description,
          isInActiveMethod: methodId === activeMethodId,
          ...getCellPresentationFields({
            isArchived,
            archiveReason,
            isLocked: isLockedByConditionGroupIndex[conditionGroupIndex],
            nodeId,
            statusByNodeId,
            roleByNodeId,
            showPersistentSourceHandleByNodeId,
          }),
        })
      );
    });
  });

  return {columnNodes, rows};
}

/**
 * Archive-dependent node fields for one cell. Archived cells render standalone: locked, with no
 * status, role, or handle. Live cells take their lock state and status/role/handle from the maps.
 */
function getCellPresentationFields({
  isArchived,
  archiveReason,
  isLocked,
  nodeId,
  statusByNodeId,
  roleByNodeId,
  showPersistentSourceHandleByNodeId,
}: {
  isArchived: boolean;
  archiveReason?: string;
  isLocked: boolean;
  nodeId: string;
  statusByNodeId?: StatusByNodeId;
  roleByNodeId: ReadonlyMap<string, ConnectionRole>;
  showPersistentSourceHandleByNodeId: ReadonlyMap<string, boolean>;
}): CellPresentationFields {
  if (isArchived) {
    return {
      isMethodHovered: false,
      isHighlighted: false,
      isNodeHovered: false,
      isLocked: true,
      isArchived: true,
      ...(archiveReason != null ? {archiveReason} : {}),
      status: undefined,
      connectionRole: null,
      showPersistentSourceHandle: false,
    };
  }
  return {
    isMethodHovered: false,
    isHighlighted: false,
    isNodeHovered: false,
    isLocked,
    status: statusByNodeId?.get(nodeId),
    connectionRole: roleByNodeId.get(nodeId) ?? null,
    showPersistentSourceHandle: showPersistentSourceHandleByNodeId.get(nodeId) ?? false,
  };
}

/**
 * Maps {@link FlowchartEdgeSpec}s to ReactFlow edges wired to each endpoint's deterministic handles.
 *
 * An edge is marked removable when its source is the upstream column and its target is the active
 * layer, matching the same position that allows drawing. The edge component gates removal on edit mode.
 */
export function makeFlowchartEdges(
  edgeSpecs: Array<FlowchartEdgeSpec>,
  roleByNodeId: ReadonlyMap<string, ConnectionRole> = new Map()
): Array<Edge<ProcedureRunDesignFlowchartEdgeData>> {
  return edgeSpecs.map((spec) => {
    const isRemovable =
      roleByNodeId.get(spec.sourceNodeId) === 'source' && roleByNodeId.get(spec.targetNodeId) === 'target';
    return makeProcedureRunDesignFlowchartEdge(spec, {isRemovable});
  });
}

/** A node in the add-replicate preview row: the add-replicate button or a downstream ghost. */
export type ProcedureRunDesignFlowchartAddReplicateRowNode = Node<
  ProcedureRunDesignFlowchartAddReplicateNodeData | ProcedureRunDesignFlowchartGhostNodeData
>;

/**
 * Builds the add-replicate preview row: the add-replicate button in the active column, an empty ghost
 * in each downstream column, and dashed preview edges chaining button to ghost to ghost in flow order.
 * Nodes start hidden so ReactFlow can measure them before the first visible frame. Callers gate this on
 * edit mode and an active method (split-pool).
 */
export function makeAddReplicateRow({
  methods,
  activeMethodId,
}: {
  methods: Array<ExperimentMethod>;
  activeMethodId: string;
}): {
  row: Array<ProcedureRunDesignFlowchartAddReplicateRowNode>;
  previewEdgeSpecs: Array<FlowchartEdgeSpec>;
} {
  const activeAndDownstreamMethodIds = getActiveAndDownstreamMethodIds(methods, activeMethodId);
  const orderedMethods = methods.filter((method) => activeAndDownstreamMethodIds.has(method.id));

  const row = orderedMethods.map((method) =>
    hideFlowchartElementBeforeLayout(
      method.id === activeMethodId
        ? createProcedureRunDesignFlowchartAddReplicateNode({methodId: method.id})
        : createProcedureRunDesignFlowchartGhostNode({methodId: method.id})
    )
  );

  const syntheticNodeId = (methodId: string): string =>
    methodId === activeMethodId
      ? getProcedureRunDesignFlowchartAddReplicateNodeId(methodId)
      : getProcedureRunDesignFlowchartGhostNodeId(methodId);

  const previewEdgeSpecs: Array<FlowchartEdgeSpec> = [];
  for (let index = 0; index < orderedMethods.length - 1; index++) {
    previewEdgeSpecs.push({
      sourceNodeId: syntheticNodeId(orderedMethods[index].id),
      targetNodeId: syntheticNodeId(orderedMethods[index + 1].id),
      variant: 'preview',
    });
  }

  return {row, previewEdgeSpecs};
}

export function filterEdgesTouchingRemovedNodes<T>(
  edges: Array<Edge<T>>,
  removedNodeIds: ReadonlySet<string>
): Array<Edge<T>> {
  return edges.filter((edge) => !removedNodeIds.has(edge.source) && !removedNodeIds.has(edge.target));
}

export function filterEdgeSpecsTouchingRemovedNodes(
  edgeSpecs: Array<FlowchartEdgeSpec>,
  removedNodeIds: ReadonlySet<string>
): Array<FlowchartEdgeSpec> {
  return edgeSpecs.filter(
    (edgeSpec) => !removedNodeIds.has(edgeSpec.sourceNodeId) && !removedNodeIds.has(edgeSpec.targetNodeId)
  );
}

/**
 * Returns the node ids for the source node and its downstream nodes in the same row.
 * Only live/unarchived cells are included, since hidden archived cells never render.
 */
export function computeSourceAndDownstreamNodeIds(
  sourceNodeId: string,
  conditionGroups: Array<ConditionGroup>,
  methods: Array<MethodIdAndName>
): ReadonlySet<string> {
  const {
    conditionIndex,
    replicateIndex,
    methodId: sourceMethodId,
  } = parseProcedureRunDesignFlowchartNodeId(sourceNodeId);
  const conditionGroup = conditionGroups[conditionIndex];
  if (conditionGroup == null) {
    return new Set<string>();
  }

  // Rows are keyed by backend replicate number and can be sparse, so find the actual rendered row
  // rather than trusting the live-only numReplicates count.
  const replicateNumber = replicateIndex + 1;
  const renderedRow = getConditionGroupGridRows(conditionGroup, methods, false).find(
    (row) => row.replicateNumber === replicateNumber
  );
  if (renderedRow == null) {
    return new Set<string>();
  }

  // Cells are in column order and only include live cells, so the source cell and everything after it
  // are the source plus its downstream cells; hidden archived cells are already excluded.
  const sourceCellIndex = renderedRow.cells.findIndex((cell) => cell.methodId === sourceMethodId);
  if (sourceCellIndex < 0) {
    return new Set<string>();
  }
  return new Set(
    renderedRow.cells
      .slice(sourceCellIndex)
      .map((cell) =>
        getProcedureRunDesignFlowchartNodeId({conditionIndex, replicateIndex, methodId: cell.methodId})
      )
  );
}

/**
 * A directed link between two template methods, identified by their method definition version ids.
 * The planning-time intermediate between the template method graph (`ProcedureEdgeNode`s) and the
 * per-replicate {@link FlowchartEdgeSpec}s.
 */
export interface MethodLink {
  sourceMethodId: string;
  targetMethodId: string;
}

/**
 * Adapts a template flowchart's edges into {@link MethodLink}s, keeping only `METHOD_EDGE`s (the
 * method-to-method execution order) and dropping `MATERIAL_EDGE`s (material flow).
 */
export function methodLinksFromProcedureEdges(edges: Array<ProcedureEdgeNode>): Array<MethodLink> {
  return edges
    .filter((edge) => edge.type === ProcedureEdgeType.METHOD_EDGE)
    .map((edge) => ({sourceMethodId: edge.sourceId, targetMethodId: edge.targetId}));
}

interface EdgeSpecsFromMethodLinksArgs {
  conditionGroups: Array<ConditionGroup>;
  methods: Array<ExperimentMethod>;
  links: Array<MethodLink>;
  shouldIncludeCondition?: (condition: ConditionGroup, conditionIndex: number) => boolean;
  getVariantForLink?: (link: MethodLink) => ProcedureRunDesignFlowchartEdgeVariant;
}

/**
 * Fans out method-level {@link MethodLink}s into per-replicate {@link FlowchartEdgeSpec}s: each link is
 * replicated within every (condition, replicate) row, connecting the two methods' grid nodes on that
 * row. Links whose endpoints aren't among the rendered `methods` are skipped so no edge points at a
 * nonexistent node.
 *
 * `shouldIncludeCondition` optionally filters which condition rows receive edges. This is necessary for
 * when adding conditions to a procedure run, and we want to show edges for unpersisted conditions based on
 * the template procedure's method edges. Links are also filtered by each condition group's coverage map, so
 * no edge points at an omitted flowchart node.

 */
export function edgeSpecsFromMethodLinks({
  conditionGroups,
  methods,
  links,
  shouldIncludeCondition,
  getVariantForLink,
}: EdgeSpecsFromMethodLinksArgs): Array<FlowchartEdgeSpec> {
  const validMethodIds = new Set(methods.map((method) => method.id));
  const validLinks = links.filter(
    (link) => validMethodIds.has(link.sourceMethodId) && validMethodIds.has(link.targetMethodId)
  );

  // Per condition group: links surviving its coverage, or `null` when filtered out by
  // `shouldIncludeCondition`.
  const linksByConditionGroupIndex = conditionGroups.map((conditionGroup, conditionGroupIndex) => {
    if (shouldIncludeCondition != null && !shouldIncludeCondition(conditionGroup, conditionGroupIndex)) {
      return null;
    }
    const coveredMethodIds = getMethodIdsImplementingConditionGroup(conditionGroup);
    return coveredMethodIds == null
      ? validLinks
      : validLinks.filter(
          (link) => coveredMethodIds.has(link.sourceMethodId) && coveredMethodIds.has(link.targetMethodId)
        );
  });

  // Live cells only (archived cells get no edges). Emit a link only when both endpoints render.
  const edgeSpecs: Array<FlowchartEdgeSpec> = [];
  getRenderedGridRows(conditionGroups, methods, false).forEach(
    ({conditionGroupIndex, replicateIndex, cells}) => {
      const linksForCondition = linksByConditionGroupIndex[conditionGroupIndex];
      if (linksForCondition == null) {
        return;
      }
      const liveMethodIds = new Set(cells.map((cell) => cell.methodId));
      linksForCondition.forEach((link) => {
        if (!liveMethodIds.has(link.sourceMethodId) || !liveMethodIds.has(link.targetMethodId)) {
          return;
        }
        const variant = getVariantForLink?.(link);
        edgeSpecs.push({
          sourceNodeId: getProcedureRunDesignFlowchartNodeId({
            conditionIndex: conditionGroupIndex,
            replicateIndex,
            methodId: link.sourceMethodId,
          }),
          targetNodeId: getProcedureRunDesignFlowchartNodeId({
            conditionIndex: conditionGroupIndex,
            replicateIndex,
            methodId: link.targetMethodId,
          }),
          ...(variant != null ? {variant} : {}),
        });
      });
    }
  );
  return edgeSpecs;
}

/** What's needed to place a PMEI in the design grid: its template method (column) and condition-replicate (row). */
export interface MethodExecutionInstanceInfoForPlacement {
  pmeiId: string;
  methodId: string;
  conditionId: string;
  replicateNumber: number;
}

/**
 * A directed edge in a `ProcedureRun`'s runtime condition graph, from a source PMEI to a target PMEI.
 */
export interface ConditionReplicateEdge {
  sourcePmeiId: string;
  targetPmeiId: string;
  /**
   * Whether the edge row itself is archived. Set by the PMEI archive cascade when either endpoint's
   * condition-replicate is archived, so an archived edge always has at least one archived endpoint.
   */
  isArchived: boolean;
}

/** A PMEI resolved to the grid node that renders it, plus its template method ID (for readonly styling). */
export interface ResolvedPmeiNode {
  nodeId: string;
  methodId: string;
}

/**
 * Maps each persisted `ProcedureRunCondition` id to its rendered condition group index (the grid row).
 * A condition id belongs to exactly one group. Shared by the status map and edge resolver.
 */
export function buildConditionGroupIndexByConditionId(
  conditions: Array<ConditionGroup>
): ReadonlyMap<string, number> {
  return new Map<string, number>(
    conditions.flatMap((conditionGroup, index) =>
      Object.values(conditionGroup.conditionIdsByPmdvId)
        .filter((conditionId): conditionId is string => conditionId != null)
        .map((conditionId) => [conditionId, index])
    )
  );
}

/**
 * Maps each PMEI id to the grid node that renders it, skipping PMEIs whose condition isn't rendered.
 * Keyed by backend `replicateNumber`, matching the grid builders, so archived-replicate gaps resolve
 * without a dense remap.
 */
export function buildPmeiToNodeIdMap(
  infoForPlacements: Array<MethodExecutionInstanceInfoForPlacement>,
  conditions: Array<ConditionGroup>
): ReadonlyMap<string, ResolvedPmeiNode> {
  const conditionIndexById = buildConditionGroupIndexByConditionId(conditions);
  const pmeiToNode = new Map<string, ResolvedPmeiNode>();
  infoForPlacements.forEach(({pmeiId, methodId, conditionId, replicateNumber}) => {
    const conditionIndex = conditionIndexById.get(conditionId);
    if (conditionIndex == null) {
      return;
    }
    assert(
      replicateNumber >= 1,
      `PMEI ${pmeiId} has a non-1-indexed replicateNumber ${replicateNumber} for condition ${conditionId}`
    );
    pmeiToNode.set(pmeiId, {
      nodeId: getProcedureRunDesignFlowchartNodeId({
        conditionIndex,
        replicateIndex: replicateNumber - 1,
        methodId,
      }),
      methodId,
    });
  });
  return pmeiToNode;
}

/**
 * Builds an inverse lookup from buildPmeiToNodeIdMap for referencing PMEI ids by flowchart node id (for
 * example in constructing save payloads). Expects each rendered, persisted node to resolve to at most one PMEI.
 */
export function buildNodeIdToPmeiIdMap(
  infoForPlacements: Array<MethodExecutionInstanceInfoForPlacement>,
  conditions: Array<ConditionGroup>
): ReadonlyMap<string, string> {
  const pmeiToNode = buildPmeiToNodeIdMap(infoForPlacements, conditions);
  const nodeIdToPmeiId = new Map<string, string>();
  pmeiToNode.forEach(({nodeId}, pmeiId) => {
    assert(!nodeIdToPmeiId.has(nodeId), `Multiple PMEIs resolved to the same flowchart node ${nodeId}`);
    nodeIdToPmeiId.set(nodeId, pmeiId);
  });
  return nodeIdToPmeiId;
}

/** Options for `readonly` tinting relative to the active unit-op layer. */
export interface ExecutionEdgeVariantOptions {
  /** Ordered unit-op methods; used to compare the source method against the active one. */
  methods?: Array<MethodIdAndName>;
  /** Active method id, or `null` when there's no active layer. */
  activeMethodId?: string | null;
}

/**
 * Maps a runtime condition graph to {@link FlowchartEdgeSpec}s, skipping edges with an unresolved
 * endpoint. With `activeMethodId`, edges whose source is at or after the active method get
 * `readonly`; others stay `editable`. Without an active method, all edges use `readonly`.
 */
export function executionEdgeSpecsFromConditionGraph(
  edges: Array<ConditionReplicateEdge>,
  pmeiToNode: ReadonlyMap<string, ResolvedPmeiNode>,
  {methods = [], activeMethodId = null}: ExecutionEdgeVariantOptions = {}
): Array<FlowchartEdgeSpec> {
  const methodIndexById = new Map(methods.map((method, index) => [method.id, index]));
  let activeMethodIndex = -1;
  if (activeMethodId != null) {
    const index = methodIndexById.get(activeMethodId);
    assert(index != null, `Active method ${activeMethodId} is not among the provided methods`);
    activeMethodIndex = index;
  }

  const edgeSpecs: Array<FlowchartEdgeSpec> = [];
  edges.forEach((edge) => {
    const sourceNode = pmeiToNode.get(edge.sourcePmeiId);
    const targetNode = pmeiToNode.get(edge.targetPmeiId);
    if (sourceNode == null || targetNode == null) {
      return;
    }
    const variant =
      activeMethodId == null
        ? 'readonly'
        : isDownstreamOfActiveLayer(sourceNode.methodId, methodIndexById, activeMethodIndex)
          ? 'readonly'
          : 'editable';
    edgeSpecs.push({
      sourceNodeId: sourceNode.nodeId,
      targetNodeId: targetNode.nodeId,
      variant,
    });
  });
  return edgeSpecs;
}

/** True when `sourceMethodId` is at or after the active method in flow order. */
function isDownstreamOfActiveLayer(
  sourceMethodId: string,
  methodIndexById: ReadonlyMap<string, number>,
  activeMethodIndex: number
): boolean {
  if (activeMethodIndex < 0) {
    return false;
  }
  const sourceMethodIndex = methodIndexById.get(sourceMethodId);
  assert(sourceMethodIndex != null, `Source method ${sourceMethodId} is not among the provided methods`);
  return sourceMethodIndex >= activeMethodIndex;
}

/**
 * Returns the largest measured height among the given nodes, falling back to `fallbackHeight`
 * when none of them have been measured yet (height null/0). Used to position grid rows below the
 * column headers using their real rendered height rather than a static estimate.
 */
export function getMaxMeasuredNodeHeight<T>(nodes: Array<Node<T>>, fallbackHeight: number): number {
  const measuredHeights = nodes.map((node) => node.height ?? 0).filter((height) => height > 0);
  return measuredHeights.length > 0 ? Math.max(...measuredHeights) : fallbackHeight;
}

/**
 * Purely geometric grid layout. Takes measured node dimensions from ReactFlow and computes
 * absolute positions for each node. Returns repositioned rows and per-column x/width values
 * for use by recomputeColumnPositions.
 *
 * Rows may be sparse: a row need not contain a node for every column. Each node is placed in its
 * own column (resolved via `getColumnIndex`), so a method with no cell in a given row leaves an
 * aligned gap rather than shifting later cells left. `columnCount` is the total number of columns
 * (methods), so columns with no cell in any row still reserve their slot.
 *
 * Present nodes must have width/height populated by ReactFlow before calling this.
 */
export function recomputeGridPositions<T>(
  rows: Array<Array<Node<T>>>,
  columnCount: number,
  getColumnIndex: (node: Node<T>) => number,
  {horizontalGap, verticalGap, startX, startY}: GridLayoutOptions
): {
  repositionedRows: Array<Array<Node<T>>>;
  columnDimensions: Array<{x: number; width: number}>;
} {
  // Only short-circuit on zero columns. With columns but no rows (0 non-archived replicates), the
  // logic below still reserves one fallback-width dimension per column for the column overlay pass.
  if (columnCount === 0) {
    return {repositionedRows: rows, columnDimensions: []};
  }

  // Each column is as wide as its widest cell across all rows. Columns with no cell keep the
  // fallback width so they still reserve a slot and stay aligned (leaving a visible gap).
  const columnWidths = Array.from({length: columnCount}, () => FALLBACK_NODE_WIDTH);
  rows.forEach((row) => {
    row.forEach((node) => {
      const columnIndex = getColumnIndex(node);
      assertValidColumnIndex(node.id, columnIndex, columnCount);
      columnWidths[columnIndex] = Math.max(columnWidths[columnIndex], node.width ?? FALLBACK_NODE_WIDTH);
    });
  });
  const columnDimensions = computeColumnDimensions(columnWidths, {startX, horizontalGap});

  // Reposition rows with cumulative Y, using the tallest node in each row.
  let currentY = startY;
  const repositionedRows = rows.map((row) => {
    const rowHeight =
      row.length > 0
        ? Math.max(
            ...row.map((node) =>
              node.height != null && node.height > 0 ? node.height : FALLBACK_NODE_HEIGHT
            )
          )
        : FALLBACK_NODE_HEIGHT;
    const repositionedRow = row.map((node) => {
      const columnIndex = getColumnIndex(node);
      assertValidColumnIndex(node.id, columnIndex, columnCount);
      return {
        ...node,
        position: {x: columnDimensions[columnIndex].x, y: currentY},
      };
    });
    currentY += rowHeight + verticalGap;
    return repositionedRow;
  });

  return {repositionedRows, columnDimensions};
}

function assertValidColumnIndex(nodeId: string, columnIndex: number, columnCount: number): void {
  assert(
    Number.isInteger(columnIndex) && columnIndex >= 0 && columnIndex < columnCount,
    `Node ${nodeId} resolved to invalid column index ${String(columnIndex)}. Expected an integer in [0, ${columnCount - 1}].`
  );
}

/**
 * Lays out the given column widths left-to-right, advancing x by `width + horizontalGap` per column.
 * Single source of truth for column x-spacing, using the widths from the widest grid cell per column.
 */
function computeColumnDimensions(
  widths: Array<number>,
  {startX, horizontalGap}: {startX: number; horizontalGap: number}
): Array<{x: number; width: number}> {
  let currentX = startX;
  return widths.map((width) => {
    const dimension = {x: currentX, width};
    currentX += width + horizontalGap;
    return dimension;
  });
}

/**
 * Positions and sizes column overlay nodes using the column dimensions computed by
 * recomputeGridPositions. Width matches the widest node in each column; height is that column's own
 * height so columns without a cell in a lower band (e.g. the add-replicate row) stop early.
 */
export function recomputeColumnPositions<T>(
  columnNodes: Array<Node<T>>,
  columnDimensions: Array<{x: number; width: number}>,
  {startY, columnHeights}: {startY: number; columnHeights: Array<number>}
): Array<Node<T>> {
  assert(
    columnNodes.length === columnDimensions.length,
    'columnNodes and columnDimensions must have the same length'
  );
  assert(
    columnNodes.length === columnHeights.length,
    'columnNodes and columnHeights must have the same length'
  );

  return columnNodes.map((node, index) => {
    const dimension = columnDimensions[index];
    return {
      ...node,
      position: {x: dimension.x, y: startY},
      style: {...node.style, width: dimension.width, height: columnHeights[index]},
    };
  });
}

/**
 * Per-column overlay heights from the column top (columnStartY) through each column's own lowest cell.
 * A column with no cell in a lower band stops at its last real cell instead of matching the tallest
 * column, so the add-replicate row only extends the active and downstream columns that contain it.
 */
export function computeColumnHeights<T>(
  repositionedRows: Array<Array<Node<T>>>,
  columnCount: number,
  getColumnIndex: (node: Node<T>) => number,
  columnStartY: number
): Array<number> {
  const heights = Array.from({length: columnCount}, () => FALLBACK_COLUMN_NODE_HEIGHT);
  const columnHasCell = Array.from({length: columnCount}, () => false);
  repositionedRows.forEach((row) => {
    row.forEach((node) => {
      const columnIndex = getColumnIndex(node);
      assertValidColumnIndex(node.id, columnIndex, columnCount);
      const nodeHeight = node.height != null && node.height > 0 ? node.height : FALLBACK_NODE_HEIGHT;
      const bottom = node.position.y + nodeHeight - columnStartY;
      if (!columnHasCell[columnIndex] || bottom > heights[columnIndex]) {
        heights[columnIndex] = bottom;
        columnHasCell[columnIndex] = true;
      }
    });
  });
  return heights;
}
